import { useState, useEffect } from 'react';
import { MessageCircle, Phone, X } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

export function FloatingChatIcons() {
  const [isOpen, setIsOpen] = useState(false);
  const [showNotification, setShowNotification] = useState(true);
  const { t } = useTranslation();

  // Auto show notification after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowNotification(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Hide notification when user interacts
  useEffect(() => {
    if (isOpen) {
      setShowNotification(false);
    }
  }, [isOpen]);

  const openZalo = () => {
    const message = `Xin chào! Tôi muốn được tư vấn về dịch vụ triển khai ứng dụng.`;
    const zaloUrl = `https://zalo.me/0961801543?text=${encodeURIComponent(message)}`;
    window.open(zaloUrl, '_blank');
  };

  const openFacebook = () => {
    const facebookUrl = 'https://www.facebook.com/profile.php?id=61577741002396';
    window.open(facebookUrl, '_blank');
  };

  const openPhone = () => {
    const phoneUrl = 'tel:0961801543';
    window.open(phoneUrl, '_self');
  };

  return (
    <div className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50">
      {/* Notification Bubble */}
      {showNotification && !isOpen && (
        <div className="absolute -top-4 -left-24 md:-left-32 bg-emerald-500 text-white px-3 py-2 md:px-4 rounded-lg text-xs md:text-sm whitespace-nowrap shadow-lg animate-bounce">
          <div className="flex items-center space-x-2">
            <MessageCircle className="w-4 h-4" />
            <span>{t('chat.need_support')}</span>
          </div>
          <button
            onClick={() => setShowNotification(false)}
            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
          >
            <X className="w-3 h-3" />
          </button>
          <div className="absolute right-0 top-1/2 transform -translate-y-1/2 border-4 border-transparent border-l-emerald-500"></div>
        </div>
      )}

      {/* Chat Icons */}
      <div className={`flex flex-col space-y-3 transition-all duration-300 ${isOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
        {/* Phone */}
        <button
          onClick={openPhone}
          className="group relative w-12 h-12 md:w-14 md:h-14 bg-green-500 hover:bg-green-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 flex items-center justify-center"
                  title={t('chat.call_phone')}
        >
          <Phone className="w-5 h-5 md:w-6 md:h-6 text-white" />
          <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2 w-3 h-3 md:w-4 md:h-4 bg-red-500 rounded-full animate-bounce">
            <div className="absolute inset-0 bg-red-500 rounded-full animate-ping"></div>
          </div>
        </button>

        {/* Facebook */}
        <button
          onClick={openFacebook}
          className="group relative w-12 h-12 md:w-14 md:h-14 bg-blue-600 hover:bg-blue-700 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 flex items-center justify-center"
          title={t('chat.facebook_messenger')}
        >
          <svg className="w-5 h-5 md:w-6 md:h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2 w-3 h-3 md:w-4 md:h-4 bg-red-500 rounded-full animate-bounce">
            <div className="absolute inset-0 bg-red-500 rounded-full animate-ping"></div>
          </div>
        </button>

        {/* Zalo */}
        <button
          onClick={openZalo}
          className="group relative w-12 h-12 md:w-14 md:h-14 bg-blue-500 hover:bg-blue-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 flex items-center justify-center"
          title={t('chat.zalo')}
        >
          <svg className="w-5 h-5 md:w-6 md:h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10c1.381 0 2.693-.258 3.902-.72l3.098 1.098c.4.141.855-.1.855-.52v-2.23c.52-.258.989-.591 1.406-.978C21.742 16.895 22 14.5 22 12c0-5.524-4.476-10-10-10zM8.5 7.5c.828 0 1.5.672 1.5 1.5s-.672 1.5-1.5 1.5S7 9.828 7 9s.672-1.5 1.5-1.5zm7 0c.828 0 1.5.672 1.5 1.5s-.672 1.5-1.5 1.5-1.5-.672-1.5-1.5.672-1.5 1.5-1.5z"/>
          </svg>
          <div className="absolute -top-1 -right-1 md:-top-2 md:-right-2 w-3 h-3 md:w-4 md:h-4 bg-red-500 rounded-full animate-bounce">
            <div className="absolute inset-0 bg-red-500 rounded-full animate-ping"></div>
          </div>
        </button>
      </div>

      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-14 h-14 md:w-16 md:h-16 bg-emerald-500 hover:bg-emerald-600 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 flex items-center justify-center group"
                title={t('chat.contact_support')}
      >
        <div className="relative">
          <MessageCircle className={`w-6 h-6 md:w-8 md:h-8 text-white transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`} />
          <div className="absolute -top-1 -right-1 w-2 h-2 md:w-3 md:h-3 bg-yellow-400 rounded-full animate-ping"></div>
          <div className="absolute -top-1 -right-1 w-2 h-2 md:w-3 md:h-3 bg-yellow-400 rounded-full animate-pulse"></div>
        </div>
      </button>

      {/* Tooltip */}
      {!isOpen && (
        <div className="absolute right-20 top-1/2 transform -translate-y-1/2 bg-slate-900 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  {t('chat.contact_support')}
          <div className="absolute left-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-l-slate-900"></div>
        </div>
      )}
    </div>
  );
}
